import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

/**
 * Raw admin progress payload for a Monthly Challenge campaign.
 * Returned as-is from the API — consumers should run it through
 * `normalizeUserProgressResponse` (see `CampaignMonthGroup`) so
 * alternate envelopes (`groups`, `task_progress`, etc.) still yield counts.
 */
export type GamePassUserCampaignProgressResponse = {
  success?: boolean;
  message?: string;
  data?: {
    enrollment?: Record<string, unknown>;
    progress?: ReadonlyArray<Record<string, unknown>>;
    challenges?: ReadonlyArray<Record<string, unknown>>;
    [key: string]: unknown;
  };
  progress?: ReadonlyArray<Record<string, unknown>>;
  challenges?: ReadonlyArray<Record<string, unknown>>;
  enrollment?: Record<string, unknown>;
  [key: string]: unknown;
};

const buildUserProgressUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_PROGRESS.replace("{user_id}", userId);

type UseGetGamePassUserCampaignProgressOptions = {
  enabled?: boolean;
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/progress?campaign_id=…`
 * Returns the axios response body directly.
 */
export const useGetGamePassUserCampaignProgress = (
  userId?: string,
  campaignId?: string,
  options?: UseGetGamePassUserCampaignProgressOptions,
): UseQueryResult<GamePassUserCampaignProgressResponse, Error> =>
  useQuery<GamePassUserCampaignProgressResponse, Error>({
    // Include `"campaign"` so this key never collides with season progress for
    // the same id. Monthly enrollments often set `season_id` = campaign id;
    // Seasons then caches `…/progress?season_id=…` under the old shared key and
    // Monthly skipped the network call with `campaign_id` (FE-2544).
    queryKey: [
      "game-pass",
      "users",
      "progress",
      userId ?? "",
      "campaign",
      campaignId ?? "",
    ],
    queryFn: async () => {
      const response =
        await getPrivateInstance().get<GamePassUserCampaignProgressResponse>(
          buildUserProgressUrl(userId!),
          { params: { campaign_id: campaignId } },
        );
      return response.data;
    },
    enabled:
      Boolean(userId && campaignId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });
